import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Loader2, Send, Bot, ChevronLeft, Paperclip, Star, X } from "lucide-react";

const FILE_URL = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/support/files/${p}`;

const statusBadge = (s) => {
  const map = {
    open: "bg-chart-4/15 text-chart-4 border-chart-4/30",
    answered: "bg-primary/15 text-primary border-primary/30",
    closed: "bg-white/5 text-muted-foreground border-white/10",
  };
  return <Badge className={`border capitalize ${map[s] || map.closed}`}>{s === "open" ? "awaiting reply" : s}</Badge>;
};

const Messages = () => {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [attach, setAttach] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data.tickets)).catch(() => toast.error("Could not load messages")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/support/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttach(data);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const rate = async (stars) => {
    if (!active) return;
    setRating(stars);
    try { const { data } = await api.post(`/support/tickets/${active.id}/rate`, { rating: stars }); setActive(data); toast.success("Thanks for your feedback! ⭐"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit rating"); }
  };

  const open = async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setActive(data);
      await api.post(`/support/tickets/${id}/read`).catch(() => {});
      setTickets((ts) => ts.map((t) => (t.id === id ? { ...t, unread: false } : t)));
    } catch { toast.error("Could not open conversation"); }
  };

  const send = async () => {
    if ((!reply.trim() && !attach) || !active) return;
    setSending(true);
    try {
      const payload = { body: reply.trim() || "(image)" };
      if (attach) Object.assign(payload, { attachment_path: attach.path, attachment_name: attach.name, attachment_type: attach.type });
      const { data } = await api.post(`/support/tickets/${active.id}/messages`, payload);
      setActive(data); setReply(""); setAttach(null); toast.success("Reply sent"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
          <h1 className="font-heading text-3xl font-black tracking-tight">My Messages</h1>
        </div>
        <p className="text-muted-foreground mb-8">Your support conversations with the CloutRise team and Clouty.</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : active ? (
          <div data-testid="message-thread">
            <button onClick={() => setActive(null)} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mb-4" data-testid="back-to-messages"><ChevronLeft className="w-4 h-4" /> All messages</button>
            <div className="rounded-xl border border-border/50 bg-card p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="font-heading font-bold text-lg">{active.subject}</h2>
                {statusBadge(active.status)}
              </div>
              <div className="space-y-3 max-h-[46vh] overflow-y-auto pr-1">
                {active.messages?.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "customer" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line ${m.role === "customer" ? "bg-primary text-background rounded-br-sm" : m.role === "ai" ? "bg-primary/10 border border-primary/20 rounded-bl-sm" : "bg-white/5 border border-white/10 rounded-bl-sm"}`}>
                      <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1 flex items-center gap-1">{m.role === "ai" && <Bot className="w-3 h-3" />}{m.role === "customer" ? "You" : m.role === "ai" ? "Clouty (AI)" : "Support"}</div>
                      {m.body && m.body !== "(image)" && <div>{m.body}</div>}
                      {m.attachment_path && (
                        <a href={FILE_URL(m.attachment_path)} target="_blank" rel="noreferrer" className="block mt-2 rounded-lg overflow-hidden bg-black/30 p-1 w-fit">
                          <img src={FILE_URL(m.attachment_path)} alt={m.attachment_name || "attachment"} className="max-h-48 rounded-md" data-testid="message-attachment" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {active.status !== "closed" && (
                <div className="mt-4">
                  {attach && (
                    <div className="mb-2 inline-flex items-center gap-2 text-xs bg-white/5 border border-white/10 rounded-lg px-2 py-1" data-testid="reply-attachment-chip">
                      <img src={FILE_URL(attach.path)} alt="preview" className="w-8 h-8 rounded object-cover" />
                      <span className="max-w-[160px] truncate">{attach.name}</span>
                      <button onClick={() => setAttach(null)} data-testid="remove-attachment"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <label className="h-11 w-11 shrink-0 rounded-full border border-white/15 flex items-center justify-center cursor-pointer hover:border-primary/40" data-testid="message-attach-btn">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadFile} data-testid="message-file-input" />
                    </label>
                    <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" className="bg-white/5 border-white/10 min-h-[52px]" data-testid="message-reply-input" />
                    <Button onClick={send} disabled={sending || (!reply.trim() && !attach)} className="h-11 rounded-full bg-primary text-background font-bold px-5" data-testid="message-reply-send">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
              {active.messages?.some((m) => m.role === "admin" || m.role === "ai") && (
                <div className="mt-4 border-t border-border/50 pt-4" data-testid="csat-widget">
                  {active.rating ? (
                    <div className="text-sm text-muted-foreground flex items-center gap-1" data-testid="csat-rated">
                      You rated this <span className="text-chart-4 font-semibold">{active.rating}/5</span>
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-4 h-4 ${s <= active.rating ? "text-chart-4 fill-chart-4" : "text-muted-foreground"}`} />)}
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm font-medium mb-1">How was our support?</div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button key={s} onMouseEnter={() => setRating(s)} onMouseLeave={() => setRating(0)} onClick={() => rate(s)} data-testid={`csat-star-${s}`}>
                            <Star className={`w-6 h-6 transition-colors ${s <= rating ? "text-chart-4 fill-chart-4" : "text-muted-foreground hover:text-chart-4"}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="rounded-2xl border border-border/50 bg-card p-12 text-center text-muted-foreground" data-testid="messages-empty">
            No messages yet. Use the chat bubble in the corner and choose "Message the team" to reach us.
          </div>
        ) : (
          <div className="space-y-3" data-testid="messages-list">
            {tickets.map((t, i) => {
              const last = t.messages?.[t.messages.length - 1];
              return (
                <motion.button key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  onClick={() => open(t.id)} className="w-full text-left rounded-xl border border-border/50 bg-card p-4 hover:border-primary/40 transition-colors" data-testid={`message-item-${t.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold truncate flex items-center gap-2">
                      {t.unread && <span className="w-2 h-2 rounded-full bg-secondary shrink-0" data-testid={`unread-dot-${t.id}`} />}
                      {t.subject}
                    </div>
                    {statusBadge(t.status)}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 truncate flex items-center gap-1">
                    {last?.role === "ai" && <Bot className="w-3.5 h-3.5 text-primary" />}
                    {last?.role === "customer" ? "You: " : last?.role === "ai" ? "Clouty: " : "Support: "}{last?.body}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">{t.updated_at ? new Date(t.updated_at).toLocaleString() : ""}</div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </DashboardShell>
  );
};

export default Messages;
